import EventEmitter from 'eventemitter3';
import createDebug from 'debug';
import { VesApiHelper } from '../ApiHelper/VesApiHelper';
import { ForgetfulArray } from '../ForgetfulArray';
import { PromiseQueue } from '../PromiseQueue';
import { RecordingError } from '../Errors';
import { RetryableVideoChunkUpload } from './RetryableVideoChunkUpload';
import { EagerVideoCompletionManager } from './EagerVideoCompletionManager';
import { RawChunkUploadPreSignedUrls } from './RawChunkUploadPreSignedUrls';
import { RetryPromiseWithBackoff } from './RetryPromiseWithBackoff';
import {
  logErrorExternally,
  getRetryMillisecondsWithExponentialBackoff,
  calculateUploadProgress,
  getRecentUploadSpeed,
  getSeconds,
} from '@/functions';
import { AudioCodecs, EventNames, VideoCodecs } from '@/constants';

/**
 * @typedef {
 *   import('./RemainingRecordedVideoData').RemainingRecordedVideoData
 * } RemainingRecordedVideoData
 * @typedef {import('../VesData').VesData} VesData
 * @typedef {{
 *   ETag: string;
 *   rawChunkNumber: number
 * }} CompletedRawChunk
 */

// See the Debug Logging section of the ML's README.md for how to use this.
const debug = createDebug('durable:RecordedVideoUploader');

const { READY_TO_UPLOAD } = EventNames.RemainingRecordedVideoData;
const { CONNECTED, ERROR, PROGRESS_UPDATE, UPLOAD_SPEED_UPDATE, UPLOAD_SUCCESS } =
  EventNames.RecordedVideoUploader;

/**
 * @param {...*} args
 */
const logError = (...args) => {
  console.error('RecordedVideoUploader:', ...args);
};

/**
 * Handles uploading a Durable Video by using the VES API and S3.
 * Also handles eager video completion during the recording to speed up stopping the video and
 * seeing the preview faster.
 */
export class RecordedVideoUploader extends EventEmitter {
  /** @type {VesData} */
  vesData;

  videoSizeTotal = 0;

  videoSizeUploaded = 0;

  recentUploadSpeedDetails = new ForgetfulArray(5);

  allUploadSpeedDetails = new ForgetfulArray();

  /** @type {CompletedRawChunk[]} */
  completedRawChunks = [];

  recordingId = '';

  recordingETag = '';

  audioCodecRecorded = AudioCodecs.UNKNOWN;

  videoCodecRecorded = VideoCodecs.UNKNOWN;

  finalizingUpload = false;

  // Chunk number must start at 1, not 0
  lastRawChunkNumber = 1;

  /** @type {PerformanceMeasure[]} */
  performanceMeasures = [];

  /** @type {PromiseQueue} */
  uploads;

  /** @type {EagerVideoCompletionManager|null} */
  eagerCompletion = null;

  eagerCompletionAttempts = 1;

  /** @type {number|null} */
  eagerCompletionCreationTimeoutId = null;

  /**
   * @param {RemainingRecordedVideoData} remainingRecordedVideoData
   * @param {VesData} vesData
   * @param {string} videoId
   * @param {string} containerFormat
   * @param {string} authToken
   */
  constructor(remainingRecordedVideoData, vesData, videoId, containerFormat, authToken) {
    super();
    this.remainingRecordedVideoData = remainingRecordedVideoData;
    this.remainingRecordedVideoData.on(READY_TO_UPLOAD, this.uploadAgainIfDataIsReady.bind(this));
    this.vesData = vesData;
    this.videoId = videoId;
    this.containerFormat = containerFormat;
    this.authToken = authToken;
    this.vesApiHelper = new VesApiHelper(vesData, authToken);

    // optionally, users can add a concurrent count to their local storage to
    // test with more concurrency:
    //    localStorage.setItem('durable-concurrency', 3)
    /** @type {import('../PromiseQueue').PromiseQueueOptions} */
    const queueOptions = {
      concurrentPromises: 2,
    };
    const concurrencySetInStorage = Number(localStorage.getItem('durable-concurrency'));
    if (concurrencySetInStorage > 0) {
      queueOptions.concurrentPromises = concurrencySetInStorage;
    }
    this.uploads = new PromiseQueue(queueOptions);
  }

  /**
   * @returns {boolean}
   */
  canUploadAnotherChunk() {
    return !this.finalizingUpload;
  }

  /**
   * Handle creating the video recording and emitting the CONNECTED event. This
   * sets a promise, so we can know creation has completed before uploading any
   * video chunks.
   *
   * @returns {Promise<void>}
   */
  async create() {
    try {
      this.createVideoRecordingPromise = this.createVideoRecording();
      await this.createVideoRecordingPromise;
      this.emit(CONNECTED);
    } catch (error) {
      logError('Error creating video recording', error);
      this.emitCreateError(error);
    }
  }

  /**
   * Call the VES API (with retries) to create a video recording and set the recording metadata.
   *
   * @returns {Promise<void>}
   */
  async createVideoRecording() {
    try {
      const methodDebug = debug.extend('createVideoRecording');

      const retryWithBackoff = new RetryPromiseWithBackoff();
      const createData = await retryWithBackoff.run(async () => {
        methodDebug('Calling API');
        const { vesApiHelper, videoId, containerFormat } = this;
        const response = await vesApiHelper.createVideoRecording(videoId, containerFormat);
        return response;
      });

      methodDebug('createData', createData);
      this.recordingId = createData.recordingId;
      this.recordingETag = createData.recordingETag;

      const { videoId, containerFormat, recordingId, recordingETag, vesApiHelper } = this;
      this.rawChunkUploadPreSignedUrls = new RawChunkUploadPreSignedUrls(
        videoId,
        containerFormat,
        recordingId,
        recordingETag,
        vesApiHelper,
      );
      this.rawChunkUploadPreSignedUrls.addUrls(createData.rawChunkUploadPreSignedUrls);
    } catch (error) {
      logError('Error creating video recording', error);
      throw error;
    }
  }

  /**
   * When we receive the audio and video codecs, we can create an eager completion manager. Without
   * calling this function, no eager video completion will happen.
   *
   * @param {string} audioCodecRecorded
   * @param {string} videoCodecRecorded
   */
  handleReadyForEagerCompletion(audioCodecRecorded, videoCodecRecorded) {
    const methodDebug = debug.extend('handleReadyForEagerCompletion');
    methodDebug('Received the following codecs', {
      audioCodecRecorded,
      videoCodecRecorded,
    });
    this.audioCodecRecorded = audioCodecRecorded;
    this.videoCodecRecorded = videoCodecRecorded;

    // Now that we know the codecs involved, we can get ready for eager completion
    this.createEagerCompletion();
  }

  /**
   * Create an eager completion manager and tell it about any chunks that are currently ready for
   * eager completion.
   */
  createEagerCompletion() {
    const methodDebug = debug.extend('createEagerCompletion');
    if (this.finalizingUpload) {
      methodDebug('Not creating eager completion because we are finalizing upload');
      return;
    }

    methodDebug('Starting...');
    this.eagerCompletion = new EagerVideoCompletionManager(
      this.videoId,
      this.recordingId,
      this.recordingETag,
      this.vesData,
      this.containerFormat,
      this.audioCodecRecorded,
      this.videoCodecRecorded,
      this.authToken,
    );
    this.eagerCompletion.on(
      EventNames.EagerVideoCompletionManager.ToClient.CONNECTED,
      this.eagerCompletionConnectedHandler.bind(this),
    );
    this.eagerCompletion.on(
      EventNames.EagerVideoCompletionManager.ToClient.ERROR,
      this.eagerCompletionErrorHandler.bind(this),
    );

    if (this.completedRawChunks.length === 0) {
      methodDebug('No completed raw chunks to tell eager completion about');
      return;
    }

    methodDebug('Telling eager completion about completed raw chunks', {
      numberOfChunks: this.completedRawChunks.length,
    });
    // If we create an eager completion, and we already have chunks, we need to tell it about them
    for (const completedRawChunk of this.completedRawChunks) {
      this.eagerCompletion.handleReadyChunk(completedRawChunk);
    }
    methodDebug('Finished');
  }

  /**
   * When the eager completion is connected, we can update some local state.
   */
  eagerCompletionConnectedHandler() {
    const methodDebug = debug.extend('eagerCompletionConnectedHandler');
    methodDebug('Connected to VES');
    /**
     * Reset eager completion attempts after successful connect, so we can retry again quickly if
     * disconnected again later.
     */
    this.eagerCompletionAttempts = 1;
  }

  /**
   * When the eager completion emits an error, we need to handle it and create a new eager
   * completion with exponential backoff.
   *
   * @param {unknown} error
   */
  eagerCompletionErrorHandler(error) {
    logError('Error with eager completion', error);
    const methodDebug = debug.extend('eagerCompletionErrorHandler');
    if (this.eagerCompletion) {
      methodDebug('Cleaning up');
      this.eagerCompletion.off(
        EventNames.EagerVideoCompletionManager.ToClient.ERROR,
        this.eagerCompletionErrorHandler.bind(this),
      );
      this.eagerCompletion.off(
        EventNames.EagerVideoCompletionManager.ToClient.CONNECTED,
        this.eagerCompletionConnectedHandler.bind(this),
      );
      // When eager completion emits an error, it disposes itself, so we don't need to do that here.
      this.eagerCompletion = null;
    } else {
      methodDebug('No eager completion to handle error for');
      return;
    }

    this.createEagerCompletionWithExponentialBackoff();
  }

  /**
   * Create an eager completion but take exponential backoff into account here, so we don't create a
   * bunch of eager completion managers one after the other.
   */
  createEagerCompletionWithExponentialBackoff() {
    const attemptsBeforeExponentialBackoff = 2;

    const eagerCompletionRetryTimeInMs = getRetryMillisecondsWithExponentialBackoff(
      this.eagerCompletionAttempts,
      attemptsBeforeExponentialBackoff,
    );
    this.eagerCompletionAttempts++;

    const methodDebug = debug.extend('createEagerCompletionWithExponentialBackoff');
    const secondsToWait = getSeconds(eagerCompletionRetryTimeInMs);
    methodDebug('Waiting to create eager completion', {
      secondsToWait,
      eagerCompletionRetryTimeInMs,
    });
    this.eagerCompletionCreationTimeoutId = setTimeout(
      this.createEagerCompletion.bind(this),
      eagerCompletionRetryTimeInMs,
    );
  }

  /**
   * Handle uploading the raw chunk and retrying if it fails
   */
  async queueUploadWithRetries() {
    try {
      const methodDebug = debug.extend('queueUploadWithRetries');

      if (!this.recordingId) {
        throw new Error('Missing recordingId to continue uploading');
      }

      if (!this.recordingETag) {
        throw new Error('Missing recordingETag to continue uploading');
      }

      const currentChunkNumber = this.lastRawChunkNumber;
      // We increment this right away so another concurrent upload could get the right chunk number
      this.lastRawChunkNumber += 1;
      const { videoId, rawChunkUploadPreSignedUrls } = this;
      const videoChunk = this.remainingRecordedVideoData.getVideoDataChunk();
      methodDebug('currentChunkNumber to enqueue?', currentChunkNumber);
      const chunkUpload = new RetryableVideoChunkUpload(
        videoChunk,
        videoId,
        currentChunkNumber,
        rawChunkUploadPreSignedUrls,
      );

      // Add this chunk to our queue to be uploaded
      methodDebug(`Enqueuing chunk ${currentChunkNumber}`);
      const taskEnqueueDetails = this.uploads.enqueue({
        id: currentChunkNumber,
        run: () => chunkUpload.upload(),
      });
      const { taskCompletionPromise } = taskEnqueueDetails;

      taskCompletionPromise
        .then(({ result }) => {
          const chunkUploadResult = /** @type {RetryableVideoChunkUpload} */ (result);
          methodDebug(`Uploaded chunk ${chunkUploadResult.rawChunkNumber}`, { chunkUploadResult });

          if (!chunkUploadResult.ETag) {
            methodDebug('Chunk upload ETag not set: upload failed?');
            return;
          }
          const { ETag, rawChunkNumber } = chunkUploadResult;
          /** @type CompletedRawChunk */
          const completedRawChunk = {
            ETag,
            rawChunkNumber,
          };
          this.trackCompletedRawChunk(completedRawChunk);
          this.trackUploadProgress(chunkUpload);
          this.eagerCompletion?.handleReadyChunk(completedRawChunk);
        })
        .catch((error) => {
          logError('Error uploading raw chunk via promise queue', error);
          this.emitUploadError(error);
        });

      methodDebug('Enqueued upload task', {
        taskId: currentChunkNumber,
        hasRemainingData: this.remainingRecordedVideoData.hasRemainingData,
      });
    } catch (error) {
      logError('Error uploading raw chunk', error);
      throw error;
    }
  }

  /**
   * Keep track of the data for the raw chunk that has been successfully uploaded
   *
   * @param {CompletedRawChunk} chunk
   */
  trackCompletedRawChunk(chunk) {
    const methodDebug = debug.extend('trackCompletedRawChunk');
    this.completedRawChunks.push(chunk);
    methodDebug('Current details', {
      chunkJustCompleted: chunk,
      completedRawChunksSoFar: this.completedRawChunks,
    });
  }

  /**
   * Track the upload progress and emit the progress and upload speed events
   *
   * @param {RetryableVideoChunkUpload} chunk
   */
  trackUploadProgress(chunk) {
    if (!chunk.startMark || !chunk.endMark) {
      console.warn('Could not trackUploadProgress() with chunk without performance measures');
      return;
    }

    const chunkUploadSpeedDetails = {
      packetSize: chunk.videoChunk.size,
      startTime: chunk.startMark.startTime,
      endTime: chunk.endMark.startTime,
    };
    this.recentUploadSpeedDetails.push(chunkUploadSpeedDetails);
    this.allUploadSpeedDetails.push(chunkUploadSpeedDetails);
    this.performanceMeasures.push(
      performance.measure('chunk-upload', {
        start: chunk.startMark.startTime,
        end: chunk.endMark.startTime,
      }),
    );

    this.videoSizeUploaded += chunk.videoChunk.size;
    this.videoSizeTotal = this.remainingRecordedVideoData.totalVideoSizeInBytes;
    const progress = calculateUploadProgress(this.videoSizeUploaded, this.videoSizeTotal);
    if (progress < 100) {
      this.emit(PROGRESS_UPDATE, progress);
    }

    const recentUploadSpeed = getRecentUploadSpeed(this.recentUploadSpeedDetails);
    this.emit(UPLOAD_SPEED_UPDATE, recentUploadSpeed);
  }

  /**
   * Upload more video if there is enough data to upload.
   *
   * @returns {Promise<void>}
   */
  async uploadAgainIfDataIsReady() {
    const methodDebug = debug.extend('uploadAgainIfDataIsReady');
    methodDebug('Starting...');

    try {
      await this.createVideoRecordingPromise;
      if (!this.canUploadAnotherChunk() || !this.remainingRecordedVideoData.hasEnoughDataToUpload) {
        methodDebug('Cannot upload another chunk right now or there is no data to upload');
        return;
      }

      await this.queueUploadWithRetries();
    } catch (error) {
      logError('Error uploading video again if data is ready', error);
      this.emitUploadError(error);
    }
  }

  /**
   * Finish uploading the video, complete it, and emit an upload success event on success.
   *
   * @returns {Promise<void>}
   */
  async finish() {
    /**
     * Need to wait for any existing raw chunk uploads to finish first. Then we
     * want to make sure to send the last packet, which may be less than the
     * video chunk size. If it's a short video, then we'll have sent no packets,
     * and we'll need to send the remaining video data which may be less than
     * the minimum video packet size, since it's the last raw chunk. Then we
     * need to complete the video recording.
     */
    try {
      const methodDebug = debug.extend('finish');
      this.finalizingUpload = true;
      methodDebug('Starting...');

      methodDebug('Waiting for any existing raw chunk uploads to finish');
      await this.uploadRemainingVideoData();

      this.emitFinalProgressUpdate();
      this.emitFinalUploadSpeedUpdate();

      methodDebug('Completing video recording');
      const videoUrl = await this.completeVideoRecording();
      this.finalizingUpload = false;
      this.emit(UPLOAD_SUCCESS, videoUrl);
      methodDebug('Finished');
    } catch (error) {
      logError('Could not finish video upload', error);
      this.emitCompleteError(error);
    }
  }

  /**
   * Emit a progress event that we're at 100% progress.
   */
  emitFinalProgressUpdate() {
    this.emit(PROGRESS_UPDATE, 100);
  }

  /**
   * Emit an upload speed event with the total upload speed.
   */
  emitFinalUploadSpeedUpdate() {
    const totalUploadSpeedInMbps = getRecentUploadSpeed(this.allUploadSpeedDetails);
    this.emit(UPLOAD_SPEED_UPDATE, totalUploadSpeedInMbps);
    const methodDebug = debug.extend('emitFinalUploadSpeedUpdate');
    methodDebug('Total upload speed in Mbps:', totalUploadSpeedInMbps);
  }

  /**
   * Uploads the remaining video data in chunks, until all the data is uploaded.
   *
   * @returns {Promise<void>}
   */
  async uploadRemainingVideoData() {
    const methodDebug = debug.extend('uploadRemainingVideoData');
    try {
      methodDebug('Remaining video size that is left to be enqueued to be uploaded', {
        size: this.remainingRecordedVideoData.size,
      });
      while (this.remainingRecordedVideoData.hasRemainingData) {
        await this.queueUploadWithRetries();
        methodDebug('Remaining video size left to enqueue', this.remainingRecordedVideoData.size);
      }

      methodDebug('Awaiting upload completion...');
      await this.uploads.queueComplete();
      methodDebug('Upload completed');

      methodDebug('Finished', { size: this.remainingRecordedVideoData.size });
    } catch (error) {
      logError('Error uploading remaining video data', error);
      this.emitUploadError(error);
    }
  }

  /**
   * Completes the video recording and returns the video URL.
   *
   * @returns {Promise<string>}
   */
  async completeVideoRecording() {
    try {
      const { videoId, containerFormat, recordingId, recordingETag, completedRawChunks } = this;
      const methodDebug = debug.extend('completeVideoRecording');
      methodDebug('Starting...');

      clearTimeout(this.eagerCompletionCreationTimeoutId);

      if (this.eagerCompletion) {
        /**
         * The uploading process increments the lastRawChunkNumber, so for the final chunk, the
         * actual last raw chunk number is one less than the lastRawChunkNumber.
         */
        const finalChunkNumber = this.lastRawChunkNumber - 1;
        try {
          methodDebug('Attempting to finish eager video completion...', { finalChunkNumber });
          const eagerlyCompletedVideoUrl = await this.eagerCompletion.finish(finalChunkNumber);
          // The finish call above disposes of eagerCompletion, so we're done with it
          this.eagerCompletion = null;
          return eagerlyCompletedVideoUrl;
        } catch (error) {
          logError('Error eagerly completing video upload; falling back to HTTP endpoint', error);
        }
      }

      // If we could not eagerly complete with the video, dispose of eagerCompletion
      this.eagerCompletion?.dispose();
      this.eagerCompletion = null;

      const retryWithBackoff = new RetryPromiseWithBackoff();
      const videoUrl = await retryWithBackoff.run(async () => {
        methodDebug('Completing via API');
        const completeResponse = await this.vesApiHelper.completeVideoRecording(
          videoId,
          containerFormat,
          recordingId,
          recordingETag,
          completedRawChunks,
        );
        methodDebug('Response from API', completeResponse);

        return completeResponse.videoUrl;
      });

      methodDebug('videoUrl', videoUrl);
      return videoUrl;
    } catch (error) {
      logError('Error completing video upload', error);
      throw error;
    }
  }

  /**
   * Emit an error event about video recording creation having failed.
   *
   * @param {ConstructorParameters<typeof RecordingError>[2]} originalError
   */
  emitCreateError(originalError) {
    const methodDebug = debug.extend('emitCreateError');
    const message = 'An error occurred while trying to create your video recording';
    methodDebug(message, { originalError });
    const metricType = 'http_create';
    const createError = new RecordingError(message, metricType, originalError);
    this.emitError(createError);
  }

  /**
   * Emit an error event about video recording upload having failed.
   *
   * @param {ConstructorParameters<typeof RecordingError>[2]} originalError
   */
  emitUploadError(originalError) {
    const methodDebug = debug.extend('emitUploadError');
    const message = 'An error occurred while uploading your video recording';
    methodDebug(message, { originalError });
    const metricType = 'http_upload';
    const uploadError = new RecordingError(message, metricType, originalError);
    this.emitError(uploadError);
  }

  /**
   * Emit an error event about video recording completion having failed.
   *
   * @param {ConstructorParameters<typeof RecordingError>[2]} originalError
   */
  emitCompleteError(originalError) {
    const methodDebug = debug.extend('emitCompleteError');
    const message = 'An error occurred while completing your video recording';
    methodDebug(message, { originalError });
    const metricType = 'http_complete';
    const completeError = new RecordingError(message, metricType, originalError);
    this.emitError(completeError);
  }

  /**
   * Emits an error event and logs the error and recent upload speed.
   *
   * @param {RecordingError} recordingError the recording error to emit and log
   */
  emitError(recordingError) {
    this.emit(ERROR, recordingError);
    const { videoId } = this;
    logErrorExternally(
      'Unexpected error',
      recordingError,
      'RecordedVideoUploader',
      { videoId },
      `videoId:${videoId}`,
    );

    const speedDetailsLength = this.recentUploadSpeedDetails?.items?.length;
    if (typeof speedDetailsLength === 'undefined' || speedDetailsLength === 0) {
      logError('No data had been successfully uploaded when upload error occurred.');
    } else {
      const uploadSpeedInMbps = getRecentUploadSpeed(this.recentUploadSpeedDetails);
      logError(`Upload speed was ${uploadSpeedInMbps} Mbps when upload error occurred.`);
    }
  }
}
